import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Typography, Space, Alert, Spin, Row, Col, Tabs, Divider, Tag, Statistic, Progress, message, Segmented } from 'antd';
import { AreaChartOutlined, RobotOutlined, EyeOutlined, LineChartOutlined, BarChartOutlined, SaveOutlined, SearchOutlined, CheckCircleOutlined } from '@ant-design/icons';
import MiniChart from '../Chart/MiniChart';
import Dashboard from './Dashboard';
import WinRateTable from './WinRateTable';
import HeatmapTable from './HeatmapTable';
import AverageReturnTable from './AverageReturnTable';
import ValidationSplitSlider from './ValidationSplitSlider';
import { useTranslation } from 'react-i18next';
import { loadConfig } from '../../utils/config';
import { useAppTheme } from '../../theme/ThemeProvider';
import { useUsageTracking } from '../../hooks/useUsageTracking';
import { logger } from '../../utils/logger';

const { Title, Text, Paragraph } = Typography;

interface Rectangle {
  startIndex: number;
  endIndex: number;
  startTime: string;
  endTime: string;
  startPrice?: number;
  endPrice?: number;
}

interface SimilarityAnalysisProps {
  ohlcData: any[];
  selectedRectangle?: Rectangle;
  onAnalysisComplete?: (result: any) => void;
  chartApiRef?: React.RefObject<any>;
  onSaveSignal?: (analysisResult: any, selectedRectangle: Rectangle) => void;
  externalResult?: any; // Agent 提供的分析结果（用于直接渲染，避免重复请求）
  autoRunToken?: number; // Agent 触发自动运行（等价于点击 Start Analysis）
}

interface AnalysisResult {
  similarity_analysis: any;
  returns_analysis: any;
  ai_analysis: any;
}

// SavedSignal接口已移至SignalManager组件

function formatTimestamp(ts: string | number): string {
  const num = typeof ts === 'string' ? parseInt(ts) : ts;
  const date = num > 1e12 ? new Date(num) : new Date(num * 1000);
  return date.toISOString().replace('T', ' ').slice(0, 19);
}

// Similar Interval List with Discovery/Validation filter
interface SimilarIntervalListProps {
  similarityAnalysis: any;
  targetEndIndex: number;
  theme: any;
  t: any;
  onSelectSegment: (segment: any) => void;
  onShowFuture: (segment: any) => void;
}

const SimilarIntervalList: React.FC<SimilarIntervalListProps> = ({
  similarityAnalysis,
  targetEndIndex,
  theme,
  t,
  onSelectSegment,
  onShowFuture
}) => {
  const [viewMode, setViewMode] = useState<'all' | 'discovery' | 'validation'>('all');
  
  const allSegments = similarityAnalysis?.similar_segments || [];
  
  // 分割数据
  const discoverySegments = allSegments.filter((s: any) => s.end_index < targetEndIndex);
  const validationSegments = allSegments.filter((s: any) => s.end_index >= targetEndIndex);
  
  const hasDiscovery = discoverySegments.length > 0;
  const hasValidation = validationSegments.length > 0;
  
  // 获取当前要显示的数据
  const getCurrentSegments = () => {
    if (viewMode === 'discovery') return discoverySegments;
    if (viewMode === 'validation') return validationSegments;
    return allSegments;
  };
  
  const currentSegments = getCurrentSegments();

  return (
    <div style={{ padding: '16px 0' }}>
      {/* 统计信息卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card size="small" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Statistic
              title={t('analysis.targetArea')}
              value={similarityAnalysis.target_segment?.start_time?.split('T')[0]}
              valueStyle={{ color: 'white', fontSize: '16px' }}
              suffix={
                <Text style={{ color: 'white', fontSize: '16px' }}>
                  ~ {similarityAnalysis.target_segment?.end_time?.split('T')[0]}
                </Text>
              }
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <Statistic
              title={t('analysis.similarIntervalCount')}
              value={allSegments.length}
              valueStyle={{ color: 'white', fontSize: '16px' }}
              suffix={t('common.unit')}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter Selector */}
      {(hasDiscovery || hasValidation) && (
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center'
        }}>
          <Segmented
            value={viewMode}
            onChange={(value) => setViewMode(value as 'all' | 'discovery' | 'validation')}
            options={[
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>📊</span>
                    <span>{t('analysis.split.all', 'All')}</span>
                    <Tag style={{ margin: 0, fontSize: 10 }}>{allSegments.length}</Tag>
                  </div>
                ),
                value: 'all',
              },
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <SearchOutlined style={{ color: '#1890ff' }} />
                    <span style={{ color: hasDiscovery ? undefined : '#999' }}>
                      {t('analysis.split.discovery', 'Discovery')}
                    </span>
                    {hasDiscovery && (
                      <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>
                        {discoverySegments.length}
                      </Tag>
                    )}
                  </div>
                ),
                value: 'discovery',
                disabled: !hasDiscovery,
              },
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span style={{ color: hasValidation ? undefined : '#999' }}>
                      {t('analysis.split.validation', 'Validation')}
                    </span>
                    {hasValidation && (
                      <Tag color="green" style={{ margin: 0, fontSize: 10 }}>
                        {validationSegments.length}
                      </Tag>
                    )}
                  </div>
                ),
                value: 'validation',
                disabled: !hasValidation,
              },
            ]}
            style={{ background: theme.toolbarBackground }}
          />
        </div>
      )}

      {/* 相似区间列表 */}
      <div style={{ maxHeight: 250, overflowY: 'auto', background: theme.toolbarBackground, borderRadius: 8, padding: 8 }}>
        {currentSegments.length > 0 ? (
          currentSegments.slice(0, 20).map((segment: any, idx: number) => (
            <Card
              key={idx}
              size="small"
              style={{
                marginBottom: 8,
                border: `1px solid ${theme.surfaceBorder}`,
                borderRadius: 6,
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              hoverable
              styles={{ body: { padding: '12px' } }}
            >
              <Row align="middle" justify="space-between">
                <Col flex="auto">
                  <div style={{ marginBottom: 4 }}>
                    <Text strong style={{ fontSize: '13px' }}>
                      {segment.start_time?.split('T')[0]} ~ {segment.end_time?.split('T')[0]}
                    </Text>
                  </div>
                  <div>
                    <Tag color="blue" style={{ marginRight: 8 }}>
                      {t('analysis.correlationCoefficient')}: {segment.correlation?.toFixed(3)}
                    </Tag>
                    {segment.correlation > 0.9 && (
                      <Tag color="green">{t('analysis.highSimilarity')}</Tag>
                    )}
                  </div>
                </Col>
                <Col>
                  <Space>
                    <Button
                      type="primary"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => onSelectSegment(segment)}
                    >
                      {t('analysis.view')}
                    </Button>
                    <Button
                      size="small"
                      icon={<LineChartOutlined />}
                      onClick={() => onShowFuture(segment)}
                    >
                      {t('analysis.futureKline')}
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <BarChartOutlined style={{ fontSize: 24, marginBottom: 8 }} />
            <div>{t('analysis.noSimilarIntervals')}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Subsequent Return Analysis Tab with Discovery/Validation toggle
interface RectSubsequentReturnAnalysisTabProps {
  returnsAnalysis: any;
  discoveryData: any;
  validationData: any;
  theme: any;
  t: any;
}

const RectSubsequentReturnAnalysisTab: React.FC<RectSubsequentReturnAnalysisTabProps> = ({
  returnsAnalysis,
  discoveryData,
  validationData,
  theme,
  t
}) => {
  const [viewMode, setViewMode] = useState<'all' | 'discovery' | 'validation'>('all');
  
  const hasDiscovery = discoveryData?.returns_analysis && discoveryData.sample_count > 0;
  const hasValidation = validationData?.returns_analysis && validationData.sample_count > 0;
  
  // 获取当前要显示的数据
  const getCurrentData = () => {
    if (viewMode === 'discovery' && hasDiscovery) {
      return { returns_analysis: discoveryData.returns_analysis };
    }
    if (viewMode === 'validation' && hasValidation) {
      return { returns_analysis: validationData.returns_analysis };
    }
    return returnsAnalysis;
  };
  
  const currentData = getCurrentData();
  
  // 获取样本数描述
  const getSampleInfo = () => {
    if (viewMode === 'discovery') {
      return `${discoveryData?.sample_count || 0} ${t('analysis.split.samples', 'samples')}`;
    }
    if (viewMode === 'validation') {
      return `${validationData?.sample_count || 0} ${t('analysis.split.samples', 'samples')}`;
    }
    return null;
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Mode Selector */}
      {(hasDiscovery || hasValidation) && (
        <div style={{ 
          marginBottom: 20, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 16,
          flexWrap: 'wrap'
        }}>
          <Segmented
            value={viewMode}
            onChange={(value) => setViewMode(value as 'all' | 'discovery' | 'validation')}
            options={[
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>📊</span>
                    <span>{t('analysis.split.all', 'All Data')}</span>
                  </div>
                ),
                value: 'all',
              },
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <SearchOutlined style={{ color: '#1890ff' }} />
                    <span style={{ color: hasDiscovery ? undefined : '#999' }}>
                      {t('analysis.split.discovery', 'Discovery')}
                    </span>
                    {hasDiscovery && (
                      <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>
                        {discoveryData.sample_count}
                      </Tag>
                    )}
                  </div>
                ),
                value: 'discovery',
                disabled: !hasDiscovery,
              },
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span style={{ color: hasValidation ? undefined : '#999' }}>
                      {t('analysis.split.validation', 'Validation')}
                    </span>
                    {hasValidation && (
                      <Tag color="green" style={{ margin: 0, fontSize: 10 }}>
                        {validationData.sample_count}
                      </Tag>
                    )}
                  </div>
                ),
                value: 'validation',
                disabled: !hasValidation,
              },
            ]}
            style={{ background: theme.toolbarBackground }}
          />
        </div>
      )}
      
      {/* Analysis Content */}
      {currentData?.returns_analysis ? (
        <>
          <Dashboard returnsAnalysis={currentData} />
          <Divider />
          <HeatmapTable returnsAnalysis={currentData} />
          <Divider />
          <AverageReturnTable returnsAnalysis={currentData} />
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: theme.textSecondary }}>
          <div>{t('analysis.noData', 'No data')}</div>
        </div>
      )}
    </div>
  );
};

const SimilarityAnalysis: React.FC<SimilarityAnalysisProps> = ({
  ohlcData,
  selectedRectangle,
  onAnalysisComplete,
  chartApiRef,
  onSaveSignal,
  externalResult,
  autoRunToken
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useAppTheme();
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<any | null>(null);
  const [config, setConfig] = useState<any>(null);
  
  // 验证分割状态
  const [splitTime, setSplitTime] = useState<number | null>(null);
  const splitStatsRef = useRef<any>(null);
  
  // 使用次数跟踪
  const { canUse, checkAndRecordUsage, showUsageExceededMessage } = useUsageTracking();

  // 异步加载配置
  useEffect(() => {
    const loadAppConfig = async () => {
      try {
        const appConfig = await loadConfig();
        setConfig(appConfig);
      } catch (error) {
        logger.error('Failed to load config:', error);
        setError('配置加载失败');
      }
    };
    
    loadAppConfig();
  }, []);

  // Agent-provided result: render directly (avoid extra fetch)
  useEffect(() => {
    if (!externalResult) return;
    if (externalResult.similarity_analysis || externalResult.returns_analysis) {
      setAnalysisResult(externalResult as any);
      setLoading(false);
      setError(null);
      onAnalysisComplete?.(externalResult);
    }
  }, [externalResult, onAnalysisComplete]);

  const API_BASE_URL = config?.API_BASE_URL;

  const analyzeRectangle = async () => {
    if (!selectedRectangle || !ohlcData.length) {
      setError('请先选择矩形区域');
      return;
    }

    if (!config || !API_BASE_URL) {
      setError('配置未加载完成，请稍后再试');
      return;
    }

    // 立即显示 loading 状态，避免用户觉得系统没响应
    setLoading(true);
    setError(null);

    // 检查使用次数限制
    const canProceed = await checkAndRecordUsage();
    if (!canProceed) {
      setLoading(false);
      return;
    }

    try {
      // 尝试获取图表截图
      let chartImage = null;
      if (chartApiRef?.current && selectedRectangle) {
        try {
          const canvas = chartApiRef.current.getCanvas();
          const chartApi = chartApiRef.current.getChartApi && chartApiRef.current.getChartApi();
          const candleSeries = chartApiRef.current.getCandleSeries && chartApiRef.current.getCandleSeries();

          if (canvas && chartApi && candleSeries) {
            const dpr = window.devicePixelRatio || 1;
            // 用K线时间和价格区间转为像素坐标
            const x1 = chartApi.timeScale().timeToCoordinate(selectedRectangle.startTime);
            const x2 = chartApi.timeScale().timeToCoordinate(selectedRectangle.endTime);
            const y1 = candleSeries.priceToCoordinate(selectedRectangle.endPrice || 0);
            const y2 = candleSeries.priceToCoordinate(selectedRectangle.startPrice || 0);

            if (x1 != null && x2 != null && y1 != null && y2 != null) {
              const left = Math.min(x1, x2) * dpr;
              const right = Math.max(x1, x2) * dpr;
              const top = Math.min(y1, y2) * dpr;
              const bottom = Math.max(y1, y2) * dpr;
              const width = right - left;
              const height = bottom - top;

              const cropped = document.createElement("canvas");
              cropped.width = width;
              cropped.height = height;
              const ctx = cropped.getContext("2d");
              if (ctx) {
                ctx.drawImage(canvas, left, top, width, height, 0, 0, width, height);
                chartImage = cropped.toDataURL("image/png");
                // logger.log("成功截取图表区域");
                // logger.log("chartImage", chartImage);
              }
            }
          }
        } catch (err) {
          logger.warn("截图失败:", err);
        }
      }

      const requestData: any = {
        ohlc_data: ohlcData,
        rectangle: {
          start_index: selectedRectangle.startIndex,
          end_index: selectedRectangle.endIndex + 1,
          start_time: selectedRectangle.startTime,
          end_time: selectedRectangle.endTime
        },
        analysis_params: {
          top_n: 10000,  // 获取前10000个最相似区间，确保显示所有>0.8的
          min_correlation: 0.8,  // 相关系数阈值0.8
          forward_periods: [10, 20, 30, 50, 100]  // 分析周期
        },
        language: i18n.language  // 添加语言参数
      };

      // 如果有截图，添加到请求中
      if (chartImage) {
        requestData.chart_image = chartImage;
      }

      const { getApiHeaders } = await import('../../utils/apiHeaders');
      const response = await fetch(`${API_BASE_URL}/rectangle_similarity/analyze_rectangle`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setAnalysisResult(result);
        // 默认MiniChart显示目标区间
        setSelectedSegment(result.similarity_analysis.target_segment);
        onAnalysisComplete?.(result);
      } else {
        setError(result.error || '分析失败');
      }
    } catch (err) {
      setError(`分析失败: ${err instanceof Error ? err.message : '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-run (equivalent to clicking "Start Analysis") when token changes
  useEffect(() => {
    if (!autoRunToken) return;
    // Only auto-run when we have a selected rectangle and OHLC data
    if (!selectedRectangle || !ohlcData?.length) return;
    analyzeRectangle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunToken]);

  const handleSaveSignal = () => {
    logger.log('保存策略被点击:', { 
      analysisResult: !!analysisResult, 
      selectedRectangle: !!selectedRectangle,
      onSaveSignal: !!onSaveSignal,
      splitTime
    });
    
    if (!analysisResult) {
      message.error('请先完成分析后再保存策略');
      return;
    }
    
    if (!selectedRectangle) {
      message.error('请先选择矩形区域');
      return;
    }
    
    if (!onSaveSignal) {
      message.error('保存功能暂时不可用');
      return;
    }
    
    // 将 split_time 和 split_stats 附加到 analysisResult
    const enrichedResult = {
      ...analysisResult,
      validation_split: splitTime ? {
        split_time: splitTime,
        stats: splitStatsRef.current
      } : undefined
    };
    
    onSaveSignal(enrichedResult, selectedRectangle);
  };
  
  // 验证分割变化处理
  const handleSplitChange = (newSplitTime: number | null, stats: any) => {
    setSplitTime(newSplitTime);
    splitStatsRef.current = stats;
  };

  const handleShowFuture = (segment: any) => {
    if (!segment || !segment.end_index) return;
    const futureData = ohlcData
      .slice(segment.end_index, segment.end_index + 30)
      .map(d => {
        if (!d) return null;
        // 如果有 datetime 字段直接用，没有就用 time 转换
        if (d.datetime) return d;
        if (d.time) {
          // time 可能是秒级或毫秒级，通常是秒级
          const dt = new Date(
            d.time.toString().length === 13 ? d.time : d.time * 1000
          );
          return { ...d, datetime: dt.toISOString() };
        }
        return null;
      })
      .filter(d => d && d.datetime);

    const allData = [...segment.data, ...futureData].sort(
      (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
    );
    setSelectedSegment({
      ...segment,
      data: allData,
      _isFuture: true,
    });
  };

  // renderSavedSignals函数已移除，信号管理功能移至独立的SignalManager组件

  const renderTabs = () => {
    if (!analysisResult) return null;
    const { similarity_analysis, returns_analysis } = analysisResult;

    return (
      <Tabs
        defaultActiveKey="1"
        style={{ marginTop: 8 }}
        items={[
          {
            key: "1",
            label: (
              <Space>
                <EyeOutlined />
                {t('analysis.similarIntervalList')}
              </Space>
            ),
            children: (
              <SimilarIntervalList
                similarityAnalysis={similarity_analysis}
                targetEndIndex={selectedRectangle?.endIndex || 0}
                theme={theme}
                t={t}
                onSelectSegment={setSelectedSegment}
                onShowFuture={handleShowFuture}
              />
            )
          },
          {
            key: "2",
            label: (
              <Space>
                <BarChartOutlined />
                {t('analysis.subsequentReturnAnalysis')}
              </Space>
            ),
            children: (
              <RectSubsequentReturnAnalysisTab
                      returnsAnalysis={returns_analysis}
                discoveryData={returns_analysis?.discovery}
                validationData={returns_analysis?.validation}
                theme={theme}
                t={t}
              />
            )
          }
        ]}
      />
    );
  };

  // 如果配置还未加载完成，显示加载状态
  if (!config) {
    return (
      <Card
        title={
          <Space>
            <AreaChartOutlined style={{ color: '#1890ff' }} />
            <span>{t('analysis.klineSimilarityAnalysis')}</span>
          </Space>
        }
        style={{
          width: '100%',
          margin: 0,
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: theme.textSecondary }}>
            正在加载配置...
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <AreaChartOutlined style={{ color: '#1890ff' }} />
          <span>{t('analysis.klineSimilarityAnalysis')}</span>
        </Space>
      }
      style={{
        width: '100%',    // 移除maxWidth和minWidth限制，充分利用宽度
        margin: 0,        // 移除margin
        borderRadius: 12,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}
      styles={{
        body: { padding: 24 },
        header: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* 选择区域信息 */}
        <Card size="small" style={{ background: theme.toolbarBackground, borderRadius: 8 }}>
          <Row align="middle" justify="space-between">
            <Col>
              <Text strong>{t('analysis.currentSelectedArea')}: </Text>
              {selectedRectangle ? (
                <Tag color="blue" style={{ fontSize: '13px' }}>
                  {formatTimestamp(selectedRectangle.startTime)} ~ {formatTimestamp(selectedRectangle.endTime)}
                </Tag>
              ) : (
                <Tag color="default">{t('analysis.noAreaSelected')}</Tag>
              )}
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<AreaChartOutlined />}
                onClick={analyzeRectangle}
                loading={loading}
                disabled={!selectedRectangle || !canUse}
                size="large"
                style={{
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none'
                }}
              >
                {t('analysis.startAnalysis')}
              </Button>
            </Col>
          </Row>
        </Card>

        {error && (
          <Alert
            message="分析错误"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ borderRadius: 8 }}
          />
        )}

        {loading && (
          <Card style={{ textAlign: 'center', padding: '40px', background: theme.toolbarBackground }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, fontSize: '16px', color: '#666' }}>
              <RobotOutlined style={{ marginRight: 8 }} />
              {t('analysis.analyzing')}
            </div>
          </Card>
        )}

        {/* AI 分析结果 */}
        {analysisResult && analysisResult.ai_analysis && (
          <Card
            style={{
              marginBottom: 16,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 12
            }}
            bodyStyle={{ padding: 20 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <RobotOutlined style={{ fontSize: 20, marginRight: 8 }} />
              <Text strong style={{ color: 'white', fontSize: '16px' }}>
                {t('analysis.aiIntelligentAnalysis')}
              </Text>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              padding: 20,
              borderRadius: 8,
              color: 'white',
              lineHeight: 1.8,
              fontSize: '14px',
              maxHeight: '300px',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.3) transparent'
            }}>
              <div style={{
                whiteSpace: 'pre-line',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                {analysisResult.ai_analysis.analysis || '暂无AI分析结果'}
              </div>
            </div>
          </Card>
        )}

        {/* 分析结果内容 */}
        {analysisResult && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 24 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ marginTop: 20 }}>
                {/* 保存策略按钮 */}
                <div style={{ marginBottom: 16, textAlign: 'right' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveSignal}
                    disabled={!analysisResult || !selectedRectangle || !onSaveSignal}
                    style={{
                      borderRadius: 8,
                      background: (!analysisResult || !selectedRectangle || !onSaveSignal) 
                        ? '#d9d9d9' 
                        : 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                      border: 'none'
                    }}
                  >
                    {t('analysis.saveAsSignal')}
                  </Button>
                  {(!analysisResult || !selectedRectangle || !onSaveSignal) && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#999', 
                      marginTop: '4px',
                      textAlign: 'right'
                    }}>
                      {!selectedRectangle && '请先在图表上选择一个矩形区域'}
                      {!analysisResult && selectedRectangle && '请先点击"开始分析"按钮'}
                      {!onSaveSignal && analysisResult && selectedRectangle && '保存功能暂时不可用'}
                    </div>
                  )}
                </div>
                {renderTabs()}
              </div>
            </div>
            {selectedSegment && (
              <div style={{ minWidth: 520, marginTop: 111 }}>
                <MiniChart
                  data={selectedSegment.data}
                  title={
                    selectedSegment._isFuture
                      ? `${t('analysis.similarIntervalAndFutureKline')}: ${selectedSegment.start_time} ~ ${selectedSegment.data[selectedSegment.data.length - 1]?.datetime}`
                      : (selectedSegment === analysisResult.similarity_analysis.target_segment
                        ? `${t('analysis.targetArea')}: ${selectedSegment.start_time} ~ ${selectedSegment.end_time}`
                        : `${t('analysis.similarInterval')}: ${selectedSegment.start_time} ~ ${selectedSegment.end_time}`)
                  }
                  onClose={() => setSelectedSegment(analysisResult.similarity_analysis.target_segment)}
                  width={520}
                  height={300}
                  enableZoom={true}
                  largeWidth={1000}
                  largeHeight={600}
                  highlightRange={
                    selectedSegment._isFuture
                      ? { from: selectedSegment.data[0].datetime, to: selectedSegment.data[selectedSegment.data.length - 31]?.datetime }
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        )}

        {/* Modal组件已移至SignalManager组件 */}
      </Space>
    </Card>
  );
};

export default SimilarityAnalysis; 